<template>
  <div class="sm-plans">
    <billing-top
      v-if="showContent == 'default'"
      :userAccount="userAccount"
      :userPlan="userPlan"
      :creditCards="creditCards"
      :balance="accountBalance.credit_balance || 0"
      :hasBalanceHistory="accountBalance.adjustments && accountBalance.adjustments.length ? true : false"
      @change-view="showContent = $event"
    />
    
    <update-billing-info 
      v-if="showContent == 'billing-info'"
      @back="showContent = 'default'"
    />

    <plan-details
      v-if="showContent == 'plan'"
      :plan="userPlan"
      :term="userAccount.term"
      :showCancel="provider.phone && provider.email && provider.payment_gateway ? true : false"
      :hasCreditCards="!!creditCards.length"
      @back="showContent = 'default'"
      @upgrade-plan="goToPlanUpgrade"
      @cancel-plan="cancelPlan"
    />

    <template v-if="provider.payment_gateway && showContent == 'default'">
      <billing-charges
        v-if="provider.client_level != 3"
      />
      <billing-charges-lvl3
        v-else
      />
    </template>

    <adjust-history
      v-if="showContent == 'history'"
      :list="accountBalance.adjustments || []"
      :canShowMore="canShowMoreAdjustments"
      @back="showContent = 'default'"
      @get-more="getMoreAjustments"
    />

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <div
          v-if="popup.type == 'cancel'"
          class="sm-plans__popup"
        >
          <h3>Cancel Subscription</h3>
          <p>To cancel subscription please contact your administrator:</p>
          <h4 
            v-if="provider.name"
            class="sm-txt-capitalize"
          >{{provider.name}}</h4>
          <p>
            <include-svg :name="'icon-phone'" />
            <a
              :href="`tel:${provider.phone}`"
            >{{provider.phone}}</a>
          </p>
          <p>
            <include-svg :name="'icon-mail'" />
            <a
              :href="`mailto:${provider.email}`"
            >{{provider.email}}</a>
          </p>
        </div>
        <div
          v-if="popup.type == 'upgrade'"
          class="sm-plans__popup"
        >
          <h3>Can't upgrade Plan</h3>
          <p v-if="!userPlan.is_active">You are subscribed to hidden plan, please contact your administator to change plan.</p>
          <p v-else>Please contact your administator to change plan.</p>
          <h4 
            v-if="provider.name"
            class="sm-txt-capitalize"
          >{{provider.name}}</h4>
          <p>
            <include-svg :name="'icon-phone'" />
            <a
              :href="`tel:${provider.phone}`"
            >{{provider.phone}}</a>
          </p>
          <p>
            <include-svg :name="'icon-mail'" />
            <a
              :href="`mailto:${provider.email}`"
            >{{provider.email}}</a>
          </p>
        </div>
      </template>
    </popup-block>

    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import UpdateBillingInfo from '@/views/customer/pages/account/components/billing-components/UpdateBillingInfo';
import PlanDetails from '@/components/account/PlanDetails';
import Charges from '@/views/customer/pages/account/components/billing-components/Charges';
import ChargesLvl3 from '@/views/customer/pages/account/components/billing-components/ChargesLvl3';
import BillingTop from '@/components/account/BillingTop';
import {FormatPrice} from '@/utils/general-data';
import Popup from '@/components/Popup';
import Spinner from '@/components/Spinner';
import AdjustHistory from '@/components/account/AdjustHistory';

export default {
  name: 'tab-plansandbilling',
  components: {
    'update-billing-info': UpdateBillingInfo,
    'plan-details': PlanDetails,
    'popup-block': Popup,
    'billing-charges': Charges,
    'billing-charges-lvl3': ChargesLvl3,
    'billing-top': BillingTop,
    'sm-spinner': Spinner,
    'adjust-history': AdjustHistory
  },
  data() {
    return {
      showContent: 'default', // values: default, billing-info, plan, history
      popup: {
        isOpen: false,
        type: '', // values: upgrade, cancel
      },
      showSpinner: false,
      accountBalance: {}
    };
  },
  created() {
    this.showSpinner = true;
    this.getBillingInfo()
      .then(billing_info => {        
        if (this.provider.client_level == 2) {
          this.getBalanceAdjustments()
            .then(balance_adjustments => {
              this.accountBalance = balance_adjustments;
              this.showSpinner = false;
            })
        } else {
          this.showSpinner = false;
        }
      });
  },
  computed: {
    ...mapGetters({
      userAccount: 'account/userAccount',
      userPlan: 'account/userPlan',
      provider: 'provider',
      creditCards: 'account/creditCards'
    }),
    canShowMoreAdjustments() {
      if (!this.accountBalance || !this.accountBalance.page)
        return false

      return this.accountBalance.page < this.accountBalance.total_pages;
    }
  },
  methods: {
    ...mapActions({
      getBillingInfo: 'account/getAllCards',
      getBalanceAdjustments: 'account/getBalanceAdjustments'
    }),
    formatPrice: FormatPrice,
    cancelPlan() {
      this.popup.type = 'cancel';
      this.popup.isOpen = true;
      // TEMP, connect api when it will be provided
    },
    goToPlanUpgrade() {
      if (this.userPlan.is_active && this.provider.client_level != 3) {
        this.$router.push('update-plan');
      } else {
        this.popup.type = 'upgrade';
        this.popup.isOpen = true;
      }
    },
    updateAccountBalance(params) {
      this.showSpinner = true;

      this.getBalanceAdjustments(params)
        .then(res => {
          this.accountBalance = res;
          this.showSpinner = false;
        })
    },
    getMoreAjustments() {
      this.showSpinner = true;

      this.getBalanceAdjustments({
        page: this.accountBalance.page + 1
      })
        .then(res => {
          this.accountBalance.adjustments = [...this.accountBalance.adjustments, ...res.adjustments];
          this.accountBalance.page = res.page;
          this.showSpinner = false;
        })
    }
  }
};
</script>

<style lang="scss">
@use '@/assets/scss/general/mixins.scss' as *;
@use '@/assets/scss/framework/scss-variables.scss' as *;
@use '@/assets/scss/pages/customer/account/components/_plans.scss';
</style>